import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Star, X, MoreVertical } from 'lucide-react';

import style from './AdminProductDetail.module.css';

import ClientCommentList from "@/ui/pages/client/ClientCommentList/ClientCommentList";

import { deleteProduct as deleteProductService } from "@services/adminCreateProductFirebaseService";
import ProductImageCarousel from "@/ui/components/client/ProductImageCarousel/ProductImageCarousel";
import heroStyle from '@/ui/components/client/ProductDetailHero/ProductDetailHero.module.css';

import backBtn from '@/ui/assets/icons/backBtn.svg';
import favBtn from '@/ui/assets/icons/favBtn.svg';
import edit from '@/ui/assets/icons/edit.svg';
import deleteIcon from '@/ui/assets/icons/delete.svg';
import deleteComment from '@/ui/assets/icons/deleteComment.svg';

import { doc, getDoc } from "firebase/firestore";

import { db } from "@config/firebaseConfig";
import { USE_FIREBASE } from "@config/appConfig";

import { ROUTES } from '@/config/routes';
import { useScrollToTop } from '@/utils/useScrollToTop';
import { sortCommentsNewestFirst } from '@/utils/commentDates';

const calculateAverageRating = (comments) => {
    if (!comments || comments.length === 0) return 0;
    const totalStars = comments.reduce((acc, c) => acc + Number(c.stars_given || 0), 0);
    return totalStars / comments.length;
};

const AdminProductDetailPage = () => {

    const navigate = useNavigate();

    const location = useLocation();

    const { productId: productIdFromRoute } = useParams();

    const dataFromCatalog = location.state;

    const productIdFromStorage = (() => {
        try {
            return localStorage.getItem('admin_selected_product_id');
        } catch {
            return null;
        }
    })();

    const resolvedProductId =
        productIdFromRoute ||
        dataFromCatalog?.id ||
        productIdFromStorage;

    const containerRef = useScrollToTop(resolvedProductId);

    const [showOptionsMenu, setShowOptionsMenu] = useState(false);

    const [product, setProduct] = useState({

        id: resolvedProductId || 'Id',

        name: dataFromCatalog?.name || 'Nombre',

        price: dataFromCatalog?.price || 0,

        rating: dataFromCatalog?.rating || 0,

        reviews: dataFromCatalog?.reviews || 0,

        likes_count: dataFromCatalog?.likes_count || dataFromCatalog?.like_count || 0,

        description: dataFromCatalog?.description || 'Descripción',

        image_url: dataFromCatalog?.image_url || 'No hay imagen',

        image_urls: dataFromCatalog?.image_urls || (dataFromCatalog?.image_url ? [dataFromCatalog.image_url] : []),

        category: dataFromCatalog?.category || '',

        comments: dataFromCatalog?.comments || [],

        is_active: dataFromCatalog?.is_active || null,

    });

    useEffect(() => {

        if (!USE_FIREBASE || !resolvedProductId) return;

        const fetchProduct = async () => {

            try {

                const docRef = doc(
                    db,
                    "products",
                    resolvedProductId
                );

                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {

                    const freshData = docSnap.data();

                    setProduct({

                        id: resolvedProductId,

                        name: freshData.name || 'Nombre',

                        price: freshData.price || 0,

                        rating: freshData.rating || 0,

                        reviews: freshData.reviews || 0,

                        likes_count: freshData.likes_count || freshData.like_count || 0,

                        description: freshData.description || '',

                        image_url: freshData.image_url || '',

                        image_urls: freshData.image_urls || (freshData.image_url ? [freshData.image_url] : []),

                        category: freshData.category || '',

                        comments: freshData.comments || [],

                        is_active: dataFromCatalog?.is_active || null,

                    });

                }

            } catch (error) {

                console.error(
                    "Error al obtener producto actualizado:",
                    error
                );

            }

        };

        fetchProduct();

    }, [resolvedProductId, location.key]);

    const galleryImages = useMemo(() => {
        const urls = product.image_urls;
        if (Array.isArray(urls) && urls.length > 0) {
            return urls.filter(Boolean);
        }
        if (product.image_url && product.image_url !== 'No hay imagen') {
            return [product.image_url];
        }
        return [];
    }, [product.image_urls, product.image_url]);

    const renderStars = (
        rating,
        activeColor = '#FFB84D',
        inactiveColor = '#EEDBD1'
    ) => {

        return Array.from({ length: 5 }, (_, i) => (

            <Star
                key={i}
                size={22}
                fill={i < rating ? activeColor : inactiveColor}
                color={i < rating ? activeColor : inactiveColor}
                strokeWidth={1.5}
            />

        ));

    };

    const handleDeleteComment = (deletedComment) => {
        setProduct((prev) => ({
            ...prev,
            comments: (prev.comments || []).filter(
                (c) => c.user_id !== deletedComment.user_id
            ),
        }));
    };

    const handleRestoreComment = (comment) => {
        setProduct((prev) => ({
            ...prev,
            comments: sortCommentsNewestFirst([...(prev.comments || []), comment]),
        }));
    };

    const handleDelete = async (id) => {

        if (
            window.confirm(
                "¿Estás seguro de que deseas eliminar este producto?"
            )
        ) {

            try {

                await deleteProductService(id);

                navigate('/admin', {
                    state: {
                        deletedProductId: id
                    }
                });

            } catch (error) {

                console.error("Error al eliminar:", error);

            }

        }

    };

    const averageRating = calculateAverageRating(product.comments);
    const reviewsCount = product.comments?.length || 0;

    const [isExpanded, setIsExpanded] = useState(false);
    const DESCRIPTION_LIMIT = 200;
    const showToggle = product.description && product.description.length > DESCRIPTION_LIMIT;
    const displayedDescription = showToggle && !isExpanded
        ? product.description.slice(0, DESCRIPTION_LIMIT) + '...'
        : product.description;

    return (

        <div ref={containerRef} className={style.detailContainer}>

            <div className={`${style.detailHeader} ${heroStyle.detailHeader}`}>

                <div className={heroStyle.imageWrapper}>

                    <ProductImageCarousel
                        images={galleryImages}
                        alt={product.name}
                    />
                </div>

                <div className={heroStyle.heroGradient} aria-hidden="true" />

                {/* Back button */}
                <button
                    className={style.backBtn}
                    onClick={() =>
                        navigate('/admin', {
                            state: {
                                updatedProduct: product
                            }
                        })
                    }
                >

                    <img src={backBtn} alt="Volver" className={style.backIcon} />

                </button>

                {/* Three dots button */}
                <button
                    type="button"
                    className={style.optionsBtn}
                    onClick={() => setShowOptionsMenu((prev) => !prev)}
                    aria-label={showOptionsMenu ? 'Cerrar opciones' : 'Opciones del producto'}
                    aria-expanded={showOptionsMenu}
                >

                    {showOptionsMenu ? (
                        <X
                            size={22}
                            color="#3B2A25"
                            strokeWidth={2.5}
                        />
                    ) : (
                        <MoreVertical
                            size={22}
                            color="#3B2A25"
                            strokeWidth={2.5}
                        />
                    )}

                </button>

                {/* OPTIONS MENU */}
                {showOptionsMenu && (

                    <>

                        <div
                            className={style.optionsCloseOverlay}
                            onClick={() => setShowOptionsMenu(false)}
                        />

                        <div className={style.optionsMenuCard}>

                            {/* Edit option */}
                            <button
                                className={style.menuOptionRow}
                                onClick={() => {

                                    setShowOptionsMenu(false);

                                    navigate(ROUTES.ADMIN_PRODUCT_EDIT, {
                                        state: product
                                    });

                                }}
                            >

                                <img src={edit} alt="Editar" className={style.menuIcon} />

                                <span className={style.editText}>
                                    Editar producto
                                </span>

                            </button>

                            <div className={style.menuDivider} />

                            {/* Delete option */}
                            <button
                                className={style.menuOptionRow}
                                onClick={() => {

                                    setShowOptionsMenu(false);

                                    handleDelete(product.id);

                                }}
                            >

                                <img src={deleteIcon} alt="Eliminar" className={style.menuIcon} />

                                <span className={style.deleteText}>
                                    Eliminar producto
                                </span>

                            </button>

                        </div>

                    </>

                )}

            </div>

            <div className={`${style.detailInfo} ${heroStyle.detailInfoHero}`}>

                <h1>{product.name}</h1>

                {/* STARS */}
                <div className={style.starsRow}>

                    {renderStars(
                        averageRating,
                        '#FFB84D'
                    )}

                    <span className={style.reviewsCount}>
                        ({reviewsCount} {reviewsCount === 1 ? 'opinión' : 'opiniones'})
                    </span>

                </div>

                {/* LIKES */}
                <div className={style.likesInfo}>

                    <img
                        src={favBtn}
                        alt="Likes"
                        className={style.likesIcon}
                    />

                    <span className={style.likesCount}>
                        {product.likes_count || 0} Me gusta
                    </span>

                </div>

                {/* PRICE */}
                <span className={style.priceTag}>
                    $
                    {' '}
                    {Number(product.price).toLocaleString('es-AR')}
                </span>

                {/* DESCRIPTION */}
                <p className={style.description}>
                    {displayedDescription}
                    {showToggle && (
                        <button
                            type="button"
                            className={style.toggleDescriptionBtn}
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            {isExpanded ? ' Ocultar' : ' Ver más'}
                        </button>
                    )}
                </p>

                <hr />

                {/* COMMENTS */}
                <ClientCommentList
                    productId={product.id}
                    comments={product.comments}
                    deleteCommentIcon={deleteComment}
                    onDeleteComment={handleDeleteComment}
                    onRestoreComment={handleRestoreComment}
                />

            </div>

        </div>

    );

};

export default AdminProductDetailPage;